import React, { useState, useEffect } from 'react'
import axiosInstance from '../../Utils/axiosInstance'
import { backendbaseUrl } from '../../Utils/baseUrl'
import { toast } from 'react-toastify';
import { Container, Row, Col, Card, CardHeader, CardBody, Input } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { Search, AlertCircle, Terminal, Database, Code, Shield } from 'lucide-react';

function ErrorLogDetails() {
    const { id } = useParams();
    const [logData, setLogData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    useEffect(() => {
        const getLogDetails = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`${backendbaseUrl}/company/error/logs-details/?id=${id}`);
                if (response.status === 200) {
                    setLogData(response.data.data || {});
                }
            } catch (error: any) {
                if (error.response) {
                    if (error.response.status === 401) {
                        toast.error("Unauthorized");
                    } else {
                        toast.error(error.response.data.message || "An unknown error occurred");
                    }
                } else {
                    console.log("error----->", error)
                    toast.error("Network error or server is down");
                }
            } finally {
                setLoading(false);
            }
        }
        getLogDetails();
    }, [id]);

    // Function to flatten nested objects
    const flattenObject = (obj: any, prefix = '') => {
        if (!obj) return {};

        return Object.keys(obj).reduce((acc: any, key) => {
            const pre = prefix.length ? `${prefix}.` : '';
            if (
                typeof obj[key] === 'object' &&
                obj[key] !== null &&
                !Array.isArray(obj[key]) &&
                Object.keys(obj[key]).length > 0
            ) {
                Object.assign(acc, flattenObject(obj[key], `${pre}${key}`));
            } else {
                acc[`${pre}${key}`] = obj[key];
            }
            return acc;
        }, {});
    };

    // Format value for display
    const formatValue = (key: string, value: any): string => {
        if (value === null) return "null";
        if (value === undefined) return "undefined";

        if (typeof value === 'string') {
            // Try to parse JSON strings for better formatting
            if ((value.startsWith('{') && value.endsWith('}')) ||
                (value.startsWith('[') && value.endsWith(']'))) {
                try {
                    const parsed = JSON.parse(value);
                    return JSON.stringify(parsed, null, 2);
                } catch {
                    // If parsing fails, return as is
                }
            }

            // Handle tokens by truncating them
            if (key.includes('token') || key.includes('Authorization')) {
                if (value.length > 20) {
                    return `${value.substring(0, 20)}...`;
                }
            }
        }

        return String(value);
    };

    // Get appropriate icon for key
    const getKeyIcon = (key: string) => {
        if (key.includes('error') || key.includes('exception')) {
            return <AlertCircle className="text-red-500" size={18} />;
        } else if (key.includes('headers') || key.includes('request')) {
            return <Code className="text-blue-400" size={18} />;
        } else if (key.includes('data') || key.includes('response')) {
            return <Database className="text-green-400" size={18} />;
        } else if (key.includes('token') || key.includes('auth') || key.includes('password')) {
            return <Shield className="text-purple-400" size={18} />;
        } else {
            return <Terminal className="text-cyan-400" size={18} />;
        }
    };

    // Toggle row expansion
    const toggleRowExpansion = (index: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRows(newExpanded);
    };

    // Filter log entries based on search term
    const filteredEntries = !logData ? [] :
        Object.entries(flattenObject(logData))
            .filter(([key, value]) =>
                key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            );

    // Group entries by category
    const groupEntries = () => {
        const groups: { [key: string]: Array<[string, any]> } = {
            'Error': [],
            'Request': [],
            'Response': [],
            'Auth': [],
            'Other': []
        };

        filteredEntries.forEach(entry => {
            const [key] = entry;
            if (key.toLowerCase().includes('error') || key.toLowerCase().includes('exception')) {
                groups['Error'].push(entry);
            } else if (key.toLowerCase().includes('request') || key.toLowerCase().includes('headers')) {
                groups['Request'].push(entry);
            } else if (key.toLowerCase().includes('response') || key.toLowerCase().includes('data')) {
                groups['Response'].push(entry);
            } else if (key.toLowerCase().includes('token') || key.toLowerCase().includes('auth')) {
                groups['Auth'].push(entry);
            } else {
                groups['Other'].push(entry);
            }
        });

        return groups;
    };

    const getStatusColor = () => {
        if (!logData) return 'bg-gray-700';

        const status = logData.status || logData.statusCode ||
            (logData.response && logData.response.status);

        if (!status) return 'bg-gray-700';

        if (status >= 200 && status < 300) return 'bg-green-500';
        if (status >= 400 && status < 500) return 'bg-yellow-500';
        if (status >= 500) return 'bg-red-500';

        return 'bg-blue-500';
    };

    return (
        <div className="page-body bg-gray-900 text-cyan-50 min-h-screen">
            <div className="py-6">
                <Container fluid>
                    <Row>
                        <Col sm="12">
                            <Card className="border-0 shadow-lg bg-gray-800 bg-opacity-80">
                                <CardHeader className="bg-gray-900 border-b border-cyan-500">
                                    <div className="flex flex-wrap justify-between items-center">
                                        <div className="flex items-center">
                                            <div className={`rounded-full ${getStatusColor()} h-3 w-3 mr-2 animate-pulse`}></div>
                                            <h5 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                                                ERROR_LOG::ID-{id?.substring(0, 8)}
                                            </h5>
                                        </div>
                                        <div className="relative w-64 mt-2 sm:mt-0">
                                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                <Search className="text-cyan-400" size={16} />
                                            </div>
                                            <Input
                                                type="text"
                                                placeholder="SEARCH_PARAMS"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="bg-gray-700 border-cyan-500 text-cyan-100 pl-10 focus:ring-cyan-500 focus:border-cyan-500"
                                                style={{
                                                    height: '38px',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 0 5px #0ff',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-0">
                                    {loading ? (
                                        <div className="flex justify-center items-center p-16">
                                            <div className="relative">
                                                <div className="h-20 w-20 rounded-full border-t-4 border-l-4 border-cyan-400 animate-spin"></div>
                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-cyan-400 font-mono">
                                                    LOADING
                                                </div>
                                            </div>
                                        </div>
                                    ) : !logData ? (
                                        <div className="flex flex-col items-center justify-center p-10">
                                            <div className="text-5xl text-red-500 mb-4">
                                                <AlertCircle />
                                            </div>
                                            <p className="text-lg text-cyan-300 font-mono">NO_LOG_DATA_AVAILABLE</p>
                                            <p className="text-xs text-cyan-500 mt-2 font-mono">SYS::/CORE/ERROR/NULL_REFERENCE</p>
                                        </div>
                                    ) : (
                                        <div>
                                            {filteredEntries.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center p-10">
                                                    <p className="text-lg text-cyan-300 font-mono">NO_MATCH_FOUND</p>
                                                    <p className="text-xs text-cyan-500 mt-2 font-mono">QUERY::{searchTerm}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="p-4 bg-gray-800 border-b border-cyan-900">
                                                        <div className="flex flex-wrap gap-4">
                                                            {Object.entries(groupEntries()).map(([group, entries], gIndex) => (
                                                                entries.length > 0 && (
                                                                    <div key={gIndex} className="px-3 py-1 rounded bg-gray-700 text-xs font-mono flex items-center">
                                                                        <span className={`mr-2 ${group === 'Error' ? 'text-red-400' :
                                                                                group === 'Request' ? 'text-blue-400' :
                                                                                    group === 'Response' ? 'text-green-400' :
                                                                                        group === 'Auth' ? 'text-purple-400' : 'text-cyan-400'
                                                                            }`}>
                                                                            {group.toUpperCase()}
                                                                        </span>
                                                                        <span className="bg-gray-800 px-2 rounded-full text-gray-400">
                                                                            {entries.length}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="overflow-auto">
                                                        <table className="w-full">
                                                            <thead>
                                                                <tr className="bg-gray-800 text-left text-xs font-mono uppercase tracking-wider">
                                                                    <th className="p-3 w-1/3 border-b border-gray-700">
                                                                        <span className="text-cyan-400">Key</span>
                                                                    </th>
                                                                    <th className="p-3 w-2/3 border-b border-gray-700">
                                                                        <span className="text-cyan-400">Value</span>
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredEntries.map(([key, value], index) => {
                                                                    const isExpandable = formatValue(key, value).length > 100;
                                                                    const isExpanded = expandedRows.has(index);
                                                                    const displayValue = formatValue(key, value);
                                                                    const isError = key.toLowerCase().includes('error') || key.toLowerCase().includes('exception');

                                                                    return (
                                                                        <tr key={index}
                                                                            className={`
                                                                                border-b border-gray-700 hover:bg-gray-750 
                                                                                ${isError ? 'bg-red-900 bg-opacity-20' : ''}
                                                                            `}
                                                                        >
                                                                            <td className="p-3 font-mono text-xs">
                                                                                <div className="flex items-center space-x-2">
                                                                                    <span>{getKeyIcon(key)}</span>
                                                                                    <span className={`
                                                                                        ${isError ? 'text-red-300' : 'text-cyan-300'}
                                                                                    `}>
                                                                                        {key}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="p-3 font-mono text-xs">
                                                                                {isExpandable ? (
                                                                                    <div>
                                                                                        <button
                                                                                            onClick={() => toggleRowExpansion(index)}
                                                                                            className="text-blue-400 underline text-xs mb-2"
                                                                                        >
                                                                                            {isExpanded ? "Collapse" : "Expand"}
                                                                                        </button>
                                                                                        <div className={isExpanded ? "" : "max-h-20 overflow-hidden relative"}>
                                                                                            <pre className={`
                                                                                                bg-gray-900 p-3 rounded border border-gray-700 whitespace-pre-wrap
                                                                                                ${key.includes('headers') || key.includes('params') || key.includes('data')
                                                                                                    ? 'text-green-300'
                                                                                                    : isError ? 'text-red-300' : 'text-gray-300'}
                                                                                            `}>
                                                                                                {displayValue}
                                                                                            </pre>
                                                                                            {!isExpanded && (
                                                                                                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-transparent"></div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className={`
                                                                                        ${key.includes('headers') || key.includes('params') || key.includes('data')
                                                                                            ? 'text-green-300'
                                                                                            : isError ? 'text-red-300' : 'text-gray-300'}
                                                                                    `}>
                                                                                        {displayValue}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    )
}

export default ErrorLogDetails